import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  Radio, RefreshCw, Pencil, Play, Power, Camera, Cpu, Loader2, AlertTriangle,
  Download, RotateCw,
  type LucideIcon,
} from 'lucide-react'
import {
  pingScreen, resyncScreen, restartScreenApp, requestScreenshot,
  checkScreenUpdates, setScreenAutoLaunch,
} from '@meno/ui/lib/api'
import { SectionLabel } from './DeviceTelemetryPanel'
import { ConfirmDialog } from '@meno/ui/components/ui/confirm-dialog'

// Screen-identification ping + the resync/restart/screenshot device commands
// — originally lived inline in ScreenInfoDrawer.tsx (the screens-list side
// panel). Extracted so ScreenDetailsPage's Device tab can offer the exact
// same commands instead of that page having none at all (previously
// Resync/Restart/Screenshot only existed in the drawer).
export function DeviceCommandsPanel({
  publicId, isWeb, autoLaunchEnabled, onScreenshotTaken, showEditorLinks = true, onDelete, onRefresh, expired, layout = 'grid',
}: {
  publicId: string
  isWeb: boolean
  /** The device's own auto-launch switch as last reported by telemetry
   *  (Screen.auto_launch_enabled). Seeds the toggle below so its label states
   *  the real setting instead of assuming "on". Null/undefined = not reported
   *  (older player build) — the button then keeps its optimistic default. */
  autoLaunchEnabled?: boolean | null
  /** Fires once a screenshot command settles, so the caller can refresh its
   *  own screen data to pick up the new shot. */
  onScreenshotTaken?: () => void
  /** "Edit configuration" / "Open editor & preview" quick actions — on by
   *  default (the drawer needs them to jump to the full page). Turn off when
   *  this panel is already embedded IN that full page. */
  showEditorLinks?: boolean
  /** Drawer-only "Unlink Screen" shortcut. Omitted → not rendered (the
   *  detail page has its own two-choice delete flow elsewhere). */
  /** Opens the permanent-delete flow. Named onDelete, not onUnlink: it wires
   *  to DELETE /screens/{id}, and "unlink" is what Detach does — releasing the
   *  hardware and keeping the screen. Calling this one "Unlink Screen" put the
   *  most destructive action in the codebase behind the label for the gentlest
   *  one. */
  onDelete?: () => void
  /** A plain "reload my own fetched data" quick action (not a device command
   *  — the drawer re-fetches its own screen/logs payload). Omitted where the
   *  host page already has its own refresh mechanism. */
  onRefresh?: () => void
  /** Screen's license has expired — a lapsed license means the device isn't
   *  actively being served content, so "Take screenshot" would just wait on
   *  a device that has nothing new to capture. Disabled + labeled why,
   *  same signal as the preview overlay / channel summary. */
  expired?: boolean
  layout?: 'grid' | 'rows' | 'compact'
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [pinging, setPinging] = useState(false)
  const ping = async () => {
    setPinging(true)
    try {
      await pingScreen(publicId)
      toast.success(t('screens.pingOverlaySent', 'Ping flashed on the screen'))
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || t('screens.pingOverlayFailed', 'Screen must be online to ping it.'))
    } finally {
      setPinging(false)
    }
  }

  // Resync / restart app / take screenshot share the ping contract:
  // online-only, fail loudly when the device isn't listening.
  const [commandBusy, setCommandBusy] = useState<string | null>(null)
  // The device owns this flag; the dashboard mirrors it. That mirror used to
  // start at a hardcoded `true` because telemetry didn't carry the setting, so
  // on a screen whose switch was OFF the button opened reading "Turn off launch
  // on boot" — offering to do what was already done, and stating the opposite
  // of the truth. The heartbeat now reports auto_launch_enabled, so the mirror
  // is seeded from it and re-syncs whenever a fresh report disagrees. It stays
  // local state rather than being read straight off the prop because the toggle
  // flips optimistically ahead of the next heartbeat.
  const [autoLaunch, setAutoLaunch] = useState(autoLaunchEnabled ?? true)
  useEffect(() => {
    if (autoLaunchEnabled != null) setAutoLaunch(autoLaunchEnabled)
  }, [autoLaunchEnabled])
  const runCommand = async (key: string, run: () => Promise<unknown>, okMsg: string) => {
    if (commandBusy) return
    setCommandBusy(key)
    try {
      await run()
      toast.success(okMsg)
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || t('screens.commandFailed', 'Screen must be online to receive commands.'))
    } finally {
      setCommandBusy(null)
    }
  }
  const retrySync = () => runCommand('resync', () => resyncScreen(publicId),
    t('screens.resyncSent', 'Sync requested — the screen is refetching its content.'))
  const restartApp = () => runCommand('restart', () => restartScreenApp(publicId),
    t('screens.restartSent', 'Restart sent — the player is relaunching.'))
  // Restart blanks a live screen for a few seconds, so it asks first.
  const [confirmRestart, setConfirmRestart] = useState(false)
  const takeScreenshot = () => runCommand('screenshot', async () => {
    await requestScreenshot(publicId)
    // The device captures + uploads asynchronously; give it a few seconds
    // before the caller re-fetches to pick up the new shot.
    window.setTimeout(() => onScreenshotTaken?.(), 6_000)
  }, t('screens.screenshotSent', 'Screenshot requested — it appears in the preview in a few seconds.'))
  const checkUpdates = () => runCommand('updates', () => checkScreenUpdates(publicId),
    t('screens.updateCheckSent', 'Update check requested — the player is asking for a release now.'))
  // A pushed value, not a command: the device adopts it and its own drawer
  // switch reads the same preference, so both surfaces agree.
  const toggleAutoLaunch = () => runCommand(
    'autolaunch',
    // The flip happens INSIDE the awaited callback, not in a .then() on
    // runCommand: runCommand catches its own errors and does not rethrow, so a
    // .then() would fire on failure too and the button would claim a change the
    // device never received.
    async () => {
      await setScreenAutoLaunch(publicId, !autoLaunch)
      setAutoLaunch(v => !v)
    },
    autoLaunch
      ? t('screens.autoLaunchOffSent', 'Launch on boot turned off.')
      : t('screens.autoLaunchOnSent', 'Launch on boot turned on.'),
  )

  return (
    <div className="space-y-4">
      <SectionLabel>{t('screens.quickActions', 'Quick actions')}</SectionLabel>
      <div className={layout === 'rows' ? 'divide-y divide-border overflow-hidden rounded-xl border border-border' : layout === 'compact' ? 'grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-[hsl(var(--border-control))] bg-[hsl(var(--border-control))]' : 'grid grid-cols-2 gap-2'}>
        <ControlButton layout={layout} icon={pinging ? Loader2 : Radio} label={t('screens.pingScreen', 'Ping')} onClick={ping} spinning={pinging} />
        {onRefresh && (
          <ControlButton layout={layout} icon={RefreshCw} label={t('screens.refresh', 'Refresh')} onClick={onRefresh} />
        )}
        {showEditorLinks && (
          <>
            <ControlButton layout={layout} icon={Pencil} label={t('screens.editConfig', 'Edit configuration')} onClick={() => navigate(`/screens/${publicId}`)} />
            <ControlButton layout={layout} icon={Play} label={t('screens.openPreview', 'Open editor & preview')} onClick={() => navigate(`/screens/${publicId}`)} />
          </>
        )}
      </div>

      <div>
        <SectionLabel>{t('screens.deviceCommands', 'Device commands')}</SectionLabel>
        <p className="text-xs text-muted-foreground mb-2">
          {t('screens.deviceCommandsHint2', 'Commands need the screen online. Greyed-out entries are on the roadmap or not possible on this player type.')}
        </p>
        <div className={layout === 'rows' ? 'divide-y divide-border overflow-hidden rounded-xl border border-border' : layout === 'compact' ? 'grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-[hsl(var(--border-control))] bg-[hsl(var(--border-control))] xl:grid-cols-4' : 'grid grid-cols-2 gap-2'}>
          {/* Content-only refresh: the device re-fetches its manifest without
              a page reload. On web this is the "Refresh page" action (vs
              "Reload browser" below, which is a full reload). */}
          <ControlButton
            layout={layout}
            icon={commandBusy === 'resync' ? Loader2 : RefreshCw}
            label={isWeb
              ? t('screens.refreshPage', 'Refresh page')
              : t('screens.retrySync', 'Retry sync')}
            onClick={retrySync}
            spinning={commandBusy === 'resync'}
            disabled={commandBusy !== null && commandBusy !== 'resync'}
          />
          {/* Web players restart by reloading the tab — label it "Reload
              browser" for what it does. */}
          <ControlButton
            layout={layout}
            icon={commandBusy === 'restart' ? Loader2 : Power}
            label={isWeb
              ? t('screens.reloadBrowser', 'Reload browser')
              : t('screens.restartApp', 'Restart app')}
            onClick={() => setConfirmRestart(true)}
            spinning={commandBusy === 'restart'}
            disabled={commandBusy !== null && commandBusy !== 'restart'}
          />
          {/* Screenshot capture is Android-only — browsers can't reliably
              capture their own rendered output.

              HIDDEN on web rather than shown greyed out with "(N/A)". Three of
              the six controls here were Android-only, so half this grid was
              dead buttons telling a web operator what they could not do. An
              absent control says "not a thing here" more honestly than a
              disabled one, which invites clicking and reads as broken.

              Still DISABLED (not hidden) once the licence has expired: that is
              a temporary state on a screen that can capture, and the label
              says why. */}
          {!isWeb && (
            <ControlButton
              layout={layout}
              icon={commandBusy === 'screenshot' ? Loader2 : Camera}
              label={expired
                ? `${t('screens.takeScreenshot', 'Take screenshot')} (${t('screens.licenseExpiredShort', 'expired')})`
                : t('screens.takeScreenshot', 'Take screenshot')}
              onClick={takeScreenshot}
              spinning={commandBusy === 'screenshot'}
              disabled={expired || (commandBusy !== null && commandBusy !== 'screenshot')}
            />
          )}
          {/* Check for updates — the drawer's action, reachable without being
              at the screen. Android only: the web player updates by reloading,
              which "Reload browser" above already does, so on web this was a
              dead button duplicating a live one right beside it. */}
          {!isWeb && (
            <ControlButton
              layout={layout}
              icon={commandBusy === 'updates' ? Loader2 : Download}
              label={t('screens.checkUpdates', 'Check for updates')}
              onClick={checkUpdates}
              spinning={commandBusy === 'updates'}
              disabled={commandBusy !== null && commandBusy !== 'updates'}
            />
          )}
          {/* Launch on boot. NOT "set as home app" or the OEM autostart
              allow-list — those open system dialogs Android requires a human to
              accept on the device, so they stay drawer-only by necessity.
              A browser tab has no boot to launch on. */}
          {!isWeb && (
            <ControlButton
              layout={layout}
              icon={commandBusy === 'autolaunch' ? Loader2 : RotateCw}
              label={autoLaunch
                ? t('screens.autoLaunchOff', 'Turn off launch on boot')
                : t('screens.autoLaunchOn', 'Turn on launch on boot')}
              onClick={toggleAutoLaunch}
              spinning={commandBusy === 'autolaunch'}
              disabled={commandBusy !== null && commandBusy !== 'autolaunch'}
            />
          )}
          <ControlButton layout={layout} icon={Cpu} label={t('screens.diagnostics', 'Diagnostics')} disabled />
        </div>
      </div>

      <ConfirmDialog
        open={confirmRestart}
        title={isWeb
          ? t('screens.reloadBrowserConfirmTitle', 'Reload the browser player?')
          : t('screens.restartConfirmTitle', 'Restart the player app?')}
        description={t('screens.restartConfirmBody', 'The screen goes blank for a few seconds while the player restarts, then picks up its content again.')}
        confirmLabel={isWeb ? t('screens.reloadBrowser', 'Reload browser') : t('screens.restartApp', 'Restart app')}
        onConfirm={() => { setConfirmRestart(false); restartApp() }}
        onClose={() => setConfirmRestart(false)}
      />

      {onDelete && (
        <div className="pt-2 border-t border-border">
          <button
            type="button"
            onClick={onDelete}
            className="flex items-center gap-2 text-sm text-destructive hover:underline"
          >
            <AlertTriangle className="w-4 h-4" />
            {t('screens.deleteScreen', 'Delete screen')}
          </button>
        </div>
      )}
    </div>
  )
}

function ControlButton({
  icon: Icon, label, onClick, disabled, spinning, layout = 'grid',
}: {
  icon: LucideIcon; label: string; onClick?: () => void; disabled?: boolean; spinning?: boolean; layout?: 'grid' | 'rows' | 'compact'
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      className={layout === 'rows'
        ? 'flex w-full items-center gap-3 bg-card px-4 py-3 text-start text-sm font-medium transition-colors hover:bg-muted/35 disabled:cursor-not-allowed disabled:opacity-50'
        : layout === 'compact'
          ? 'flex min-h-11 items-center justify-center gap-2 bg-card px-3 py-2 text-xs font-normal text-foreground transition-colors hover:bg-muted/35 disabled:cursor-not-allowed disabled:opacity-50'
        : 'flex flex-col items-center justify-center gap-1.5 rounded-xl border border-border bg-card px-2 py-3 text-xs font-medium hover:border-primary/40 hover:bg-muted/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-border disabled:hover:bg-card'}
    >
      <Icon className={`w-4 h-4 text-muted-foreground ${spinning ? 'animate-spin' : ''}`} />
      <span className={layout === 'rows' ? 'flex-1 leading-tight' : 'text-center leading-tight'}>{label}</span>
      {layout === 'rows' && <span className="text-muted-foreground">›</span>}
    </button>
  )
}
